import {
  AuthUser,
  EnvironmentType,
  ExternalAccountType,
  FixMeLater,
  GroupData,
  LanguageNameSpaces,
  SubscriptionPlanType,
  UtmData,
  UtmParams,
} from "@shared/types";
import {
  AUTH_SLICE_PATH,
  EXCHANGES_ORDER,
  HEDGE_MODE_EXCHANGES,
  INTERCOM_ARTICLE_ID,
  LOCAL_STORAGE_UTM_DATA_KEY,
  REQUESTS_AUTH_HEADER,
} from "@shared/common/constants";
import { sharedLib } from "@shared/services/shared.service";
import {
  dateIsLifetime,
  formatDate,
  formatDateRelative,
} from "@shared/utils/date";
import moment from "moment";
import { invert } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { TranslationKeys } from "@shared/common/i18n/types";
import { AccountObjectType } from "@shared/features/settings/types";

export const hexToRGB = (hex?: string, alpha?: number) => {
  if (!hex) return;

  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
  } else {
    return "rgb(" + r + ", " + g + ", " + b + ")";
  }
};

export const getDisplayResultMessageFunction =
  (
    openMessage: (
      text: string,
      type: "error" | "success",
      duration?: number
    ) => void
  ) =>
  async (
    promise: Promise<any>,
    successMessage?: string,
    defaultError?: string,
    duration?: number,
    onSuccessCallback?: (response: any) => void,
    ignoreStatuses?: number[],
    showSuccess = true,
    useSuccessMessage?: boolean
  ) => {
    const getServerMessageOrDefault = (
      response: any,
      defaultMessage?: string
    ) => {
      if (response?.data?.message?.length) return response.data.message;
      else if (response?.message) return response?.message;
      else return defaultMessage;
    };

    try {
      const response = await promise;
      if (typeof response === "object" && "error" in response) {
        const message = getServerMessageOrDefault(response.error, defaultError);
        if (message) openMessage(message, "error");
        return Promise.reject(response.error);
      } else if (showSuccess) {
        const message = useSuccessMessage
          ? successMessage
          : getServerMessageOrDefault(response, successMessage);
        if (message) openMessage(message, "success", duration);
        onSuccessCallback?.(response);
      }
      return Promise.resolve(response);
    } catch (error) {
      const errorStatus = (error as any).status;
      if (errorStatus === 429) {
        openMessage(
          "Max number of attempts exceeded, please try again later.",
          "error"
        );
      } else if (!ignoreStatuses?.includes(errorStatus)) {
        const message = getServerMessageOrDefault(error, defaultError);
        if (message) openMessage(message, "error");
      }
      return Promise.reject(error);
    }
  };

export const checkSearch = (value?: string, searchValue?: string) => {
  if (!searchValue) return true;
  return !!value?.toLowerCase().includes(searchValue?.toLowerCase());
};

export const checkSymbolSearch = (
  symbol?: FixMeLater,
  searchValue?: string
) => {
  if (typeof symbol !== "string") return false;
  if (searchValue?.includes("/")) return checkSearch(symbol, searchValue);
  return checkSearch(symbol.replace("/", ""), searchValue);
};

export const scaleNumberToRange = (
  number: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number
) => {
  const value =
    outMin + ((number - inMin) * (outMax - outMin)) / (inMax - inMin);
  return Math.max(outMin, Math.min(value, outMax));
};

export const getNotificationDisplayTime = (
  text: string,
  inSeconds?: boolean
) => {
  return (
    scaleNumberToRange(text.length, 40, 160, 4, 8) * (inSeconds ? 1 : 1000)
  );
};

export const getAssetFromSymbol = (symbol: string) => {
  return symbol.split("/")[0];
};
export const getPairFromSymbol = (symbol: string) => {
  return symbol.split("/")[1];
};

const USD_EQUIVALENT_PAIRS = new Set([
  "USD",
  "USDT",
  "USDC",
  "TUSD",
  "PAX",
  "BUSD",
  "USDS",
  "DAI",
]);

export const getPossibleEquivalentSymbols = (symbol: string): string[] => {
  const asset = getAssetFromSymbol(symbol);
  const pair = getPairFromSymbol(symbol);
  if (!USD_EQUIVALENT_PAIRS.has(pair)) {
    return [];
  }
  return Array.from(USD_EQUIVALENT_PAIRS)
    .filter((_pair) => _pair !== pair)
    .map((_pair) => `${asset}/${_pair}`);
};

export const isContractsSymbol = (name?: string) => name?.includes("/");

export const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

export const extractTelegramUsernameFromLink = (link?: string) =>
  /(?:.*t\.me\/)(.*)/g.exec(link || "")?.[1];

export const extractIntercomArticleId = (articleLink?: string) => {
  return articleLink && INTERCOM_ARTICLE_ID.exec(articleLink)?.[1];
};

export const getExternalAccountDisplay = (
  user: AuthUser | undefined,
  accountType: ExternalAccountType
) => {
  if (!user) return {};
  let text;
  let tooltipDescription;
  const isConnected =
    accountType === ExternalAccountType.TELEGRAM
      ? !!user?.is_telegram_connected
      : !!user?.is_discord_connected;
  if (isConnected) {
    if (accountType === ExternalAccountType.TELEGRAM) {
      if (user?.username) {
        text = user?.username;
        tooltipDescription = "Telegram username";
      } else if (user?.telegram_id) {
        text = user?.telegram_id;
        tooltipDescription = "Telegram ID";
      }
    } else {
      if (user?.discord_username) {
        text = user?.discord_username;
        tooltipDescription = "Discord username";
      } else if (user?.discord_id) {
        text = user?.discord_id;
        tooltipDescription = "Discord ID";
      }
    }
  }
  return {
    text,
    tooltipDescription,
    isConnected,
  };
};

export const setStoreTokens = (token?: string, refreshToken?: string) => {
  const authSlice = sharedLib.slices[AUTH_SLICE_PATH];
  sharedLib.store.dispatch(
    authSlice.actions.setTokens({ token, refresh_token: refreshToken })
  );
};

export const clearStoreTokens = () => {
  try {
    const authSlice = sharedLib.slices[AUTH_SLICE_PATH];
    sharedLib.store.dispatch(authSlice.actions.clear());
    sharedLib.store.dispatch(sharedLib.baseApi.util.resetApiState());
  } catch {}
};

type BillingTranslate = (
  key: TranslationKeys<LanguageNameSpaces.BILLING>,
  options?: Record<string, unknown>
) => string;

export const getFormattedGroupExpiration = (
  groupData: GroupData,
  t: BillingTranslate
) => {
  if (
    !groupData.expiration_timestamp ||
    dateIsLifetime(groupData.expiration_timestamp, true)
  )
    return;
  const now = moment().unix();
  const expired = groupData.expiration_timestamp < now;
  const time = formatDateRelative(
    groupData.expiration_timestamp,
    true,
    false,
    true
  );
  return t(
    expired ? "expiration.expired_relative" : "expiration.expires_relative",
    { time }
  );
};

export const getGroupSubscriptionExpiration = (
  groupData: GroupData,
  t: BillingTranslate
) => {
  const now = moment().unix();
  if (groupData.next_auto_renewal) {
    if (groupData.next_auto_renewal < now)
      return t("group_subscriptions.pending_renewal");
    const time = formatDateRelative(groupData.next_auto_renewal, true);
    return t("expiration.renews_relative", { time });
  }
  return getFormattedGroupExpiration(groupData, t);
};

export const getUserExpirationInfo = (
  user: AuthUser | undefined,
  t: (key: TranslationKeys<LanguageNameSpaces.BILLING>) => string
) => {
  let expirationAction = t("expiration.expires");
  if (user?.has_auto_renewal) expirationAction = t("expiration.renews");
  if (user?.isExpired()) expirationAction = t("expiration.expired");

  return [
    expirationAction,
    user?.expiration_timestamp
      ? formatDate(user?.expiration_timestamp, false, true)
      : undefined,
  ];
};

export const updateItems = (
  currentItems: Set<string> | string[],
  itemsToUpdate: Set<string> | string[],
  add: boolean,
  setFunction: (items: string[]) => void
) => {
  const updatedSymbolsSet = new Set(currentItems);
  itemsToUpdate.forEach((symbol) => {
    if (add) {
      updatedSymbolsSet.add(symbol);
    } else {
      updatedSymbolsSet.delete(symbol);
    }
  });
  setFunction(Array.from(updatedSymbolsSet));
};

export const capitalizeFirstChar = (text?: string) => {
  return text
    ?.split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
    .join(" ");
};

export const getEnvironmentBackendUrl = (environment: EnvironmentType) => {
  if (environment === EnvironmentType.PROD)
    return "https://dashboard.cornix.io/app";
  else if (environment === EnvironmentType.STAGING)
    return "https://app.stage.cornix.io";
  else if (environment === EnvironmentType.TEST)
    return "https://dashboard.test.cornix.io/app";
  else if (environment === EnvironmentType.DEV)
    return "https://dashboard.dev.cornix.io/app";
  return "http://localhost:9000/app";
};

export const getEnvironmentWebsiteUrl = (environment: EnvironmentType) => {
  if (environment === EnvironmentType.PROD)
    return "https://dashboard.cornix.io";
  else if (environment === EnvironmentType.STAGING)
    return "https://dashboard-stage.cornix.io";
  else if (environment === EnvironmentType.TEST)
    return "https://dashboard.test.cornix.io";
  else if (environment === EnvironmentType.DEV)
    return "https://dashboard.dev.cornix.io";
  return "https://localhost:3000";
};

export const getEnvironmentTvWebhookUrl = (environment: EnvironmentType) => {
  return getEnvironmentWebsiteUrl(environment) + "/tradingview/";
};

export const isWhitelabelSubscriptionType = (
  subscriptionPlanType?: SubscriptionPlanType
) => {
  return (
    !!subscriptionPlanType &&
    [
      SubscriptionPlanType.ASSET_MANAGER,
      SubscriptionPlanType.COMMUNITY_ADMIN,
    ].includes(subscriptionPlanType)
  );
};

export const getSubscriptionTypeDisplay = (
  subscriptionPlanType?: SubscriptionPlanType
) => {
  if (!subscriptionPlanType) return;
  const subscriptionPlanName =
    invert(SubscriptionPlanType)[subscriptionPlanType];
  return capitalizeFirstChar(
    subscriptionPlanName?.toLowerCase().replace("_", " ")
  );
};

export const getAccountsEmptyData = (
  t: (key: TranslationKeys<LanguageNameSpaces.COMMON>) => string,
  isDemo?: boolean
) => {
  return {
    title: isDemo
      ? t("empty.no_demo_accounts")
      : t("empty.no_exchange_accounts"),
    description: isDemo
      ? t("empty.no_demo_accounts_description")
      : t("empty.no_exchange_accounts_description"),
    buttonText: isDemo
      ? t("empty.no_demo_accounts_button")
      : t("empty.no_exchange_accounts_button"),
  };
};

export const getMarketingWebsiteUrl = (environment: EnvironmentType) => {
  if (environment === EnvironmentType.PROD) return "https://cornix.io";
  return "https://website.dev.cornix.io";
};

const getLocalStorageUtmValue = () => {
  return sharedLib.storageService.getParsedValue<UtmData>(
    LOCAL_STORAGE_UTM_DATA_KEY
  );
};

export const updateLocalStorageUtmData = (utmData: UtmData) => {
  sharedLib.storageService.setJsonValue(LOCAL_STORAGE_UTM_DATA_KEY, utmData);
};

export const updateAndGetUtmData = async (
  queryParams?: Record<string, string>
) => {
  let prevUtmData = getLocalStorageUtmValue();
  if (!queryParams?.[UtmParams.UTM_SOURCE]) return prevUtmData;

  let updatedUtmData = prevUtmData ?? {
    utm_user_uuid: uuidv4(),
  };
  Object.values(UtmParams).forEach((param) => {
    updatedUtmData = { ...updatedUtmData, [param]: queryParams[param] };
  });
  updateLocalStorageUtmData(updatedUtmData);
  return updatedUtmData;
};

export const getUtmUserUuid = async () => {
  let utmUuid: string | undefined;
  try {
    utmUuid = getLocalStorageUtmValue()?.utm_user_uuid;
  } catch {}
  return utmUuid;
};

export const addInviteTokenAuthHeader = (
  params: any,
  args: { authToken?: string } | void
) => {
  if (!args || !args.authToken) return params;
  return {
    ...params,
    headers: {
      [REQUESTS_AUTH_HEADER]: `create-account-invite-token ${args.authToken}`,
    },
  };
};

export const sortByExchangeOrder = (items?: string[]) => {
  if (!items) return;
  return [...items]?.sort((a, b) => {
    const aIndex = (EXCHANGES_ORDER as any)[a] || 0;
    const bIndex = (EXCHANGES_ORDER as any)[b] || 0;
    return aIndex - bIndex;
  });
};

export const sortItemsByExchangeOrder = <T extends { exchange: string }>(
  items?: T[]
) => {
  if (!items) return;
  return [...items]?.sort((a, b) => {
    const aIndex = (EXCHANGES_ORDER as any)[a.exchange] || 0;
    const bIndex = (EXCHANGES_ORDER as any)[b.exchange] || 0;
    return aIndex - bIndex;
  });
};

export const isHedgeModeSupported = (exchange: string): boolean =>
  HEDGE_MODE_EXCHANGES.has(exchange);

export const getReadOnlyAccountWarning = (objectType: AccountObjectType) => {
  return `This ${objectType} is ${
    objectType === AccountObjectType.ACCOUNT
      ? "read-only"
      : "connected to a read-only account"
  } and can only be modified by its associated sub-user.`;
};
